import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styles from './SignatureInput.module.css';
import SignaturePad from 'signature_pad';
import { Button } from '../../components/Button';
import EraseIcon from '../../assets/icons/erase.svg?react';
import { useIntl } from 'react-intl';
import { dataURItoFile, fileToUrl } from './utils';

export interface SignatureInputProps {
  onClear: () => void;
  onChange: (file: File) => void;
  value?: File | string;
  filename?: string;
}

export const SignatureInput: FC<SignatureInputProps> = ({
  onClear,
  onChange,
  filename = 'signature',
  value,
}) => {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  const { $t } = useIntl();

  const signaturePad = useMemo(() => {
    if (canvas) {
      return new SignaturePad(canvas);
    }
    return null;
  }, [canvas]);

  const resizeCanvas = useCallback(() => {
    if (!canvas || !signaturePad) {
      return;
    }
    const ratio = Math.max(window.devicePixelRatio || 1, 1);

    canvas.width = (canvas.parentElement || canvas).offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    signaturePad.fromData(signaturePad.toData());
  }, [canvas, signaturePad]);

  useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
  }, [resizeCanvas]);

  useEffect(() => {
    const onSign = () => {
      if (signaturePad) {
        onClear();
        onChange(
          dataURItoFile(signaturePad.toDataURL('image/png'), `${filename}.jpg`)
        );
      }
    };
    signaturePad?.addEventListener('endStroke', onSign);

    return () => {
      signaturePad?.removeEventListener('endStroke', onSign);
      signaturePad?.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signaturePad, filename]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (value && signaturePad) {
      if (typeof value !== 'string') {
        fileToUrl(value, signal)
          .then((url) => signaturePad.fromDataURL(url))
          .catch(() => {});
      } else {
        signaturePad.fromDataURL(value);
      }
    }

    return () => {
      controller.abort('Unmounting');
    };
  }, [signaturePad, value]);

  return (
    <div className={styles.wrapper}>
      <canvas ref={setCanvas} data-testid="signature-canvas" />
      <Button
        className={styles.clearBtn}
        variant="text"
        onClick={() => {
          onClear();
          signaturePad?.clear();
        }}
        title={$t({ id: 'Clear' })}
      >
        <EraseIcon className={styles.erase} />
      </Button>
    </div>
  );
};
